import { useEffect, useState } from "react";
import {
  Button,
  FormField,
  Input,
  StatusBadge,
  Textarea,
} from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { Select } from "@/components/ui/Select";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import {
  useDeleteOverride,
  usePromptDefinition,
  useSaveOverride,
} from "@/hooks/prompts/usePrompts";
import { useDateFormatter } from "@/utils/datetime";
import { promptKeyDisplayLabel } from "./promptKeyLabels";
import type { PromptDefinitionEditor, SavePromptOverrideRequest } from "@/types/prompts";

type TriBool = "inherit" | "true" | "false";
type HttpChoice = "inherit" | "Anthropic" | "AnthropicLong";

interface FormState {
  model: string;
  temperature: string;
  maxTokens: string;
  caching: TriBool;
  httpClient: HttpChoice;
  body: string;
}

const EMPTY: FormState = {
  model: "",
  temperature: "",
  maxTokens: "",
  caching: "inherit",
  httpClient: "inherit",
  body: "",
};

function fromDefinition(def: PromptDefinitionEditor): FormState {
  const o = def.override;
  return {
    model: o?.model ?? "",
    temperature: o?.temperature != null ? String(o.temperature) : "",
    maxTokens: o?.maxTokens != null ? String(o.maxTokens) : "",
    caching: o?.enablePromptCaching == null ? "inherit" : o.enablePromptCaching ? "true" : "false",
    httpClient: (o?.httpClientName as HttpChoice) ?? "inherit",
    body: o?.systemPromptBody ?? "",
  };
}

function toRequest(form: FormState, bodyOverridable: boolean): SavePromptOverrideRequest {
  return {
    model: form.model.trim() || null,
    temperature: form.temperature.trim() === "" ? null : Number(form.temperature),
    maxTokens: form.maxTokens.trim() === "" ? null : Number(form.maxTokens),
    enablePromptCaching: form.caching === "inherit" ? null : form.caching === "true",
    httpClientName: form.httpClient === "inherit" ? null : form.httpClient,
    systemPromptBody: bodyOverridable && form.body.trim() ? form.body : null,
  };
}

function validate(form: FormState): { temperature?: string; maxTokens?: string } {
  const errors: { temperature?: string; maxTokens?: string } = {};
  if (form.temperature.trim() !== "") {
    const t = Number(form.temperature);
    if (Number.isNaN(t) || t < 0 || t > 1) errors.temperature = "Must be between 0 and 1.";
  }
  if (form.maxTokens.trim() !== "") {
    const n = Number(form.maxTokens);
    if (!Number.isInteger(n) || n <= 0) errors.maxTokens = "Must be a positive whole number.";
  }
  return errors;
}

/**
 * Per-call-site editor for the global (definition-level) prompt override. Every
 * field defaults to "inherit the code default"; a value here overrides it for that
 * call site everywhere. Config (model / sampling / tokens / caching / HTTP client)
 * takes effect immediately; the prompt body is editable only for the static-literal
 * prompts (the code-owned single-string bodies).
 */
export function PromptOverrideEditor({
  promptKey,
  onClose,
}: {
  promptKey: string | null;
  onClose: () => void;
}) {
  const toast = useToast();
  const fmt = useDateFormatter();
  const query = usePromptDefinition(promptKey);
  const save = useSaveOverride();
  const reset = useDeleteOverride();
  const def = query.data;

  const [form, setForm] = useState<FormState>(EMPTY);
  const [initedFor, setInitedFor] = useState<string | null>(null);

  useEffect(() => {
    if (promptKey == null) {
      setInitedFor(null);
    }
  }, [promptKey]);

  useEffect(() => {
    if (def && def.key !== initedFor) {
      setForm(fromDefinition(def));
      setInitedFor(def.key);
    }
  }, [def, initedFor]);

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const errors = validate(form);
  const hasErrors = Object.keys(errors).length > 0;
  const hasOverride = def?.override != null;

  const handleSave = () => {
    if (!def || hasErrors) return;
    void save
      .mutateAsync({ key: def.key, body: toRequest(form, def.bodyOverridable) })
      .then(() => {
        toast.success("Override saved.");
        onClose();
      })
      .catch((e) => toast.error(extractErrorMessage(e)));
  };

  const handleReset = () => {
    if (!def) return;
    void reset
      .mutateAsync(def.key)
      .then(() => {
        toast.success("Override removed.");
        onClose();
      })
      .catch((e) => toast.error(extractErrorMessage(e)));
  };

  return (
    <Modal
      open={promptKey != null}
      onClose={onClose}
      size="xl"
      title={promptKey == null ? "Prompt" : promptKeyDisplayLabel(promptKey)}
      description={def?.description}
      footer={
        <div className="flex w-full items-center justify-between">
          <Button
            variant="text"
            onClick={handleReset}
            loading={reset.isPending}
            disabled={!hasOverride || save.isPending}
          >
            Reset to default
          </Button>
          <div className="flex items-center gap-space-3">
            <Button variant="secondary" onClick={onClose} disabled={save.isPending || reset.isPending}>
              Cancel
            </Button>
            <Button onClick={handleSave} loading={save.isPending} disabled={hasErrors || !def}>
              Save override
            </Button>
          </div>
        </div>
      }
    >
      {query.isLoading || !def ? (
        <div className="text-gray-mid">Loading…</div>
      ) : (
        <div className="flex flex-col gap-space-4">
          <div className="flex flex-wrap items-center gap-space-2">
            <StatusBadge tone="neutral">{def.bodySource}</StatusBadge>
            {hasOverride ? (
              <StatusBadge tone="info">Overridden</StatusBadge>
            ) : (
              <span className="text-caption text-gray-mid">Running on the code default</span>
            )}
          </div>

          <p className="text-caption text-gray-mid">
            Leave a field blank / “Inherit” to use the code default. A value here overrides that
            field for this call site everywhere; changes take effect on the next call.
          </p>

          <div className="grid grid-cols-1 gap-space-4 md:grid-cols-2">
            <FormField
              label="Model"
              htmlFor="ov-model"
              helperText={`Default: ${def.model}`}
            >
              <Input
                id="ov-model"
                value={form.model}
                placeholder={def.model}
                onChange={(e) => set("model", e.target.value)}
              />
            </FormField>

            <FormField
              label="Temperature"
              htmlFor="ov-temp"
              helperText={`Default: ${def.temperature}`}
              error={errors.temperature}
            >
              <Input
                id="ov-temp"
                type="number"
                step="0.1"
                min={0}
                max={1}
                value={form.temperature}
                placeholder={String(def.temperature)}
                invalid={!!errors.temperature}
                onChange={(e) => set("temperature", e.target.value)}
              />
            </FormField>

            <FormField
              label="Max tokens"
              htmlFor="ov-maxtokens"
              helperText={`Default: ${def.maxTokens}`}
              error={errors.maxTokens}
            >
              <Input
                id="ov-maxtokens"
                type="number"
                min={1}
                value={form.maxTokens}
                placeholder={String(def.maxTokens)}
                invalid={!!errors.maxTokens}
                onChange={(e) => set("maxTokens", e.target.value)}
              />
            </FormField>

            <FormField
              label="Prompt caching"
              htmlFor="ov-caching"
              helperText={`Default: ${def.enablePromptCaching ? "on" : "off"}`}
            >
              <Select
                id="ov-caching"
                value={form.caching}
                onChange={(e) => set("caching", e.target.value as TriBool)}
              >
                <option value="inherit">Inherit ({def.enablePromptCaching ? "on" : "off"})</option>
                <option value="true">On</option>
                <option value="false">Off</option>
              </Select>
            </FormField>

            <FormField
              label="HTTP client"
              htmlFor="ov-http"
              helperText={`Default: ${def.httpClientName ?? "Anthropic (standard)"}`}
            >
              <Select
                id="ov-http"
                value={form.httpClient}
                onChange={(e) => set("httpClient", e.target.value as HttpChoice)}
              >
                <option value="inherit">Inherit ({def.httpClientName ?? "standard"})</option>
                <option value="Anthropic">Anthropic (standard, ~120s)</option>
                <option value="AnthropicLong">AnthropicLong (background, ~360s)</option>
              </Select>
            </FormField>
          </div>

          <FormField
            label="Prompt body"
            htmlFor="ov-body"
            helperText={
              def.bodyOverridable
                ? "Overrides the built-in prompt text. Leave blank to keep the code default shown below."
                : "This prompt's text is assembled at runtime or authored per template — only its configuration is overridable here."
            }
          >
            {def.bodyOverridable ? (
              <div className="flex flex-col gap-space-2">
                <Textarea
                  id="ov-body"
                  rows={12}
                  className="font-mono text-caption"
                  value={form.body}
                  placeholder={def.defaultBody ?? ""}
                  onChange={(e) => set("body", e.target.value)}
                />
                {def.defaultBody != null && (
                  <div className="flex items-center justify-between gap-space-2">
                    <span className="text-caption text-gray-mid">
                      {form.body.trim() ? "Custom body in use." : "Using the code default."}
                    </span>
                    <Button
                      variant="text"
                      onClick={() => set("body", def.defaultBody ?? "")}
                      disabled={form.body === (def.defaultBody ?? "")}
                    >
                      Load default text
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <div className="rounded-md border border-border-light bg-white-warm p-space-3">
                <span className="text-caption text-gray-mid">Not editable for this prompt.</span>
              </div>
            )}
          </FormField>

          {def.override?.modifiedAt && (
            <p className="text-caption text-gray-mid">
              Override last saved {fmt.dateTime(def.override.modifiedAt)}
              {def.override.modifiedByUserId ? ` by ${def.override.modifiedByUserId}` : ""}.
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}
